import React, { useState } from "react";
import { buildUserMap, parseCommentMentions } from "./utils/mentionUtils";

import Link from "next/link";
import { sanitizeContent } from "./utils/commentSecurity";
import { useFeatureGate } from "statsig-react";

interface CommentContentProps {
  content: string;
  availableUsers?: Map<string, { id: string; username: string }>;
  className?: string;
}

const TRUNCATE_LENGTH = 150; // Character limit for truncation

export default function CommentContent({
  content,
  availableUsers = new Map(),
  className = "text-sm text-gray-900 mb-2 whitespace-pre-wrap",
}: CommentContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Feature gate for user profiles
  const { gate: videoCommentsGate } = useFeatureGate("video_comments_stableid");
  const userProfilesEnabled = videoCommentsGate?.value ?? false;

  // Sanitize content first
  const sanitizedContent = sanitizeContent(content);

  // Check if content needs truncation
  const needsTruncation = sanitizedContent.length > TRUNCATE_LENGTH;

  // Get the content to display (truncated or full)
  const displayContent =
    needsTruncation && !isExpanded
      ? sanitizedContent.slice(0, TRUNCATE_LENGTH)
      : sanitizedContent;

  // Helper function to render content with or without mentions
  const renderContent = (text: string) => {
    // Parse mentions if user profiles are enabled
    if (!userProfilesEnabled || availableUsers.size === 0) {
      return text;
    }

    const parsed = parseCommentMentions(text, availableUsers);

    if (parsed.mentions.length === 0) {
      return text;
    }

    // Build JSX elements with clickable mentions
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;

    parsed.mentions.forEach((mention, index) => {
      // Add text before mention
      if (mention.start > lastIndex) {
        elements.push(
          <span key={`text-${index}`}>
            {text.slice(lastIndex, mention.start)}
          </span>
        );
      }

      // Add clickable mention
      elements.push(
        <Link
          key={`mention-${index}`}
          href={`/u/${mention.userId}`}
          className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
        >
          @{mention.username}
        </Link>
      );

      lastIndex = mention.end;
    });

    // Add remaining text after last mention
    if (lastIndex < text.length) {
      elements.push(<span key="text-end">{text.slice(lastIndex)}</span>);
    }

    return elements;
  };

  return (
    <div>
      <p className={className}>
        {renderContent(displayContent)}
        {needsTruncation && !isExpanded && "... "}
        {needsTruncation && !isExpanded && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors inline"
          >
            Read more
          </button>
        )}
      </p>
      {needsTruncation && isExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
        >
          Show Less
        </button>
      )}
    </div>
  );
}
